import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { resolveOrganizationDestination } from "@/lib/organizationRouting";
import { useLanguage } from "@/contexts/LanguageContext";
import { buildPublicAssetUrl } from "@/lib/appPaths";
import { localizeAuthMessage } from "@/lib/authMessages";
import { sendEmailVerificationCode, verifyEmailCode, signUpWithPassword } from "@/lib/authApi";
import { validateInviteCode } from "@/lib/invitationApi";
import { CheckCircle, Gift, KeyRound, Lock, Mail, User, UserRoundPlus } from "lucide-react";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";

export default function Signup() {
  const { t, language } = useLanguage();
  const { signIn } = useAuth();
  const [, setLocation] = useLocation();

  // Step 1 state
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Step 2 state
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [inviteCodeMessage, setInviteCodeMessage] = useState("");
  const [isInviteCodeValid, setIsInviteCodeValid] = useState<boolean | null>(null);
  const [isValidatingInviteCode, setIsValidatingInviteCode] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Blob animation refs (same as Login)
  const bgRef = useRef<HTMLDivElement>(null);
  const blob1Ref = useRef<HTMLDivElement>(null);
  const blob2Ref = useRef<HTMLDivElement>(null);
  const blob3Ref = useRef<HTMLDivElement>(null);
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef([
    { x: 0, y: 0 },
    { x: 0, y: 0 },
    { x: 0, y: 0 },
  ]);
  const rafRef = useRef<number | null>(null);

  const blobs = [
    { ref: blob1Ref, strength: 60,  ease: 0.06 },
    { ref: blob2Ref, strength: -90, ease: 0.04 },
    { ref: blob3Ref, strength: 45,  ease: 0.08 },
  ];

  const normalizeInviteCode = useCallback((value: string) => value.replace(/[\s-]+/g, "").toUpperCase(), []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("invite_code") || params.get("ref") || "";
    if (code) {
      setInviteCode(normalizeInviteCode(code));
    }
  }, [normalizeInviteCode]);

  useEffect(() => {
    const animate = () => {
      blobs.forEach(({ ref, strength, ease }, i) => {
        const cur = currentRef.current[i];
        const tgt = targetRef.current;
        cur.x += (tgt.x * strength - cur.x) * ease;
        cur.y += (tgt.y * strength - cur.y) * ease;
        if (ref.current) {
          ref.current.style.transform = `translate(${cur.x}px, ${cur.y}px)`;
        }
      });
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const el = bgRef.current;
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    targetRef.current = {
      x: (e.clientX - left) / width - 0.5,
      y: (e.clientY - top) / height - 0.5,
    };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleSendCode = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorMessage(t("请填写邮箱", "Please enter your email."));
      return;
    }
    setErrorMessage("");
    setIsSendingCode(true);
    try {
      const result = await sendEmailVerificationCode(trimmedEmail);
      if (!result.ok) {
        setErrorMessage(localizeAuthMessage(language, t("发送失败，请稍后重试", "Failed to send code. Please try again."), result.message));
        return;
      }
      setCodeSent(true);
      setCooldown(60);
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedCode = verificationCode.trim();
    if (!trimmedEmail || !trimmedCode) {
      setErrorMessage(t("请填写邮箱和验证码", "Please enter your email and verification code."));
      return;
    }
    setErrorMessage("");
    setSuccessMessage("");
    setIsVerifyingCode(true);
    try {
      const result = await verifyEmailCode(trimmedEmail, trimmedCode);
      if (!result.ok || !result.verificationToken) {
        setErrorMessage(localizeAuthMessage(language, t("验证码错误或已过期", "Invalid or expired verification code."), result.message));
        return;
      }
      setVerificationToken(result.verificationToken);
      setStep(2);
      setErrorMessage("");
      setSuccessMessage("");
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const validateCurrentInviteCode = useCallback(async () => {
    const normalizedCode = normalizeInviteCode(inviteCode);
    setInviteCode(normalizedCode);
    if (!normalizedCode) {
      setInviteCodeMessage("");
      setIsInviteCodeValid(null);
      return true;
    }

    setIsValidatingInviteCode(true);
    try {
      const result = await validateInviteCode(normalizedCode);
      setInviteCodeMessage(result.message);
      setIsInviteCodeValid(result.valid);
      return result.valid;
    } catch (error) {
      console.error("Validate invite code failed:", error);
      setInviteCodeMessage(t("这个邀请码暂不可用。你可以修改邀请码，或清空后继续注册。", "This invite code is unavailable. Edit it or clear it to continue."));
      setIsInviteCodeValid(false);
      return false;
    } finally {
      setIsValidatingInviteCode(false);
    }
  }, [inviteCode, normalizeInviteCode, t]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      setErrorMessage(t("两次输入的密码不一致", "Passwords do not match."));
      return;
    }
    if (inviteCode.trim()) {
      const inviteOk = await validateCurrentInviteCode();
      if (!inviteOk) {
        setErrorMessage(t("这个邀请码暂不可用。你可以修改邀请码，或清空后继续注册。", "This invite code is unavailable. Edit it or clear it to continue."));
        return;
      }
    }
    setErrorMessage("");
    setSuccessMessage("");
    setIsSubmitting(true);
    try {
      const result = await signUpWithPassword({
        email: email.trim(),
        username: username.trim(),
        password,
        fullName: fullName.trim(),
        verificationToken,
        inviteCode: normalizeInviteCode(inviteCode),
      });

      if (!result.ok) {
        setErrorMessage(
          localizeAuthMessage(
            language,
            t("注册失败，请稍后重试", "Sign up failed. Please try again."),
            result.message
          )
        );
        return;
      }

      const loginResult = await signIn({
        identifier: username.trim(),
        password,
        rememberMe: true,
      });

      if (!loginResult.ok) {
        setSuccessMessage(
          t(
            "注册成功，请使用刚创建的账号登录。",
            "Account created. Please sign in with your new account."
          )
        );
        setTimeout(() => {
          setLocation("/login");
        }, 600);
        return;
      }

      const rewardAmount = Number(result.inviteReward?.amount || 0);
      setSuccessMessage(
        rewardAmount > 0
          ? t(
              "注册成功，邀请权益已记录，正在进入工作台...",
              "Account created. Invite benefit recorded. Entering workspace..."
            )
          : t("注册成功，正在进入工作台...", "Account created. Entering workspace...")
      );
      setTimeout(() => {
        setLocation(resolveOrganizationDestination("/", loginResult.user?.organization?.code), { replace: true });
      }, 250);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      ref={bgRef}
      className="fixed inset-0 flex items-center justify-center overflow-y-auto"
      onMouseMove={handleMouseMove}
      style={{ backgroundColor: "#f4f9f4" }}
    >
      <style>{`
        @keyframes tm-breathe-1 { 0%,100% { opacity:1; } 50% { opacity:0.45; } }
        @keyframes tm-breathe-2 { 0%,100% { opacity:1; } 50% { opacity:0.35; } }
        @keyframes tm-breathe-3 { 0%,100% { opacity:1; } 50% { opacity:0.50; } }
      `}</style>
      {/* 光晕1：左上 */}
      <div ref={blob1Ref} style={{
        position: "absolute", left: "12%", top: "25%", width: "65%", height: "55%",
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(96,175,106,0.18) 0%, transparent 70%)",
        animation: "tm-breathe-1 2s ease-in-out infinite",
      }} />
      {/* 光晕2：右上 */}
      <div ref={blob2Ref} style={{
        position: "absolute", right: "12%", top: "12%", width: "60%", height: "70%",
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(140,205,145,0.14) 0%, transparent 65%)",
        animation: "tm-breathe-2 2.5s ease-in-out infinite 0.4s",
      }} />
      {/* 光晕3：底部 */}
      <div ref={blob3Ref} style={{
        position: "absolute", left: "15%", bottom: "-10%", width: "80%", height: "55%",
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(110,188,118,0.12) 0%, transparent 70%)",
        animation: "tm-breathe-3 1.8s ease-in-out infinite 0.2s",
      }} />

      <div className="w-full max-w-[360px] px-4 flex flex-col items-center gap-7 py-10 mb-16">
        {/* Logo + Title */}
        <div className="flex flex-col items-center gap-3">
          <img
            src={buildPublicAssetUrl("/logo_character.png")}
            alt="TeachMaster"
            className="w-24 object-contain"
          />
          <div className="text-center space-y-1">
            <h1 className="text-2xl font-medium text-foreground">
              {step === 1 ? t("验证邮箱", "Verify Email") : t("创建账户", "Create Account")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {step === 1
                ? t("输入邮箱并完成验证，开始注册流程。", "Enter and verify your email to begin registration.")
                : t("填写账号信息，完成注册。", "Fill in your account details to complete sign-up.")}
            </p>
          </div>
        </div>

        {/* Step 1: Email Verification */}
        {step === 1 ? (
          <form onSubmit={handleVerifyCode} className="w-full flex flex-col gap-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                <Input
                  id="signup-email"
                  type="email"
                  placeholder={t("输入邮箱", "Enter email")}
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (errorMessage) setErrorMessage("");
                    if (codeSent) {
                      setCodeSent(false);
                      setVerificationCode("");
                    }
                  }}
                  className="h-11 rounded-xl pl-9 bg-white border-border/50 shadow-sm"
                  disabled={isSendingCode}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                className="h-11 shrink-0 rounded-xl shadow-sm"
                onClick={handleSendCode}
                disabled={!email.trim() || isSendingCode || cooldown > 0}
              >
                {isSendingCode
                  ? t("发送中...", "Sending...")
                  : cooldown > 0
                  ? t(`重发(${cooldown}s)`, `Resend(${cooldown}s)`)
                  : codeSent
                  ? t("重新发送", "Resend")
                  : t("发送验证码", "Send Code")}
              </Button>
            </div>

            {codeSent && (
              <div className="relative">
                <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                <Input
                  id="signup-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder={t("输入6位验证码", "Enter 6-digit code")}
                  value={verificationCode}
                  onChange={(event) => {
                    setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                    if (errorMessage) setErrorMessage("");
                  }}
                  className="h-11 rounded-xl pl-9 bg-white border-border/50 shadow-sm tracking-widest"
                  autoFocus
                />
              </div>
            )}

            {errorMessage && (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            {codeSent && (
              <Button
                type="submit"
                className="h-11 w-full rounded-xl mt-1 shadow-sm"
                disabled={verificationCode.length !== 6 || isVerifyingCode}
              >
                {isVerifyingCode ? t("验证中...", "Verifying...") : t("验证并继续", "Verify & Continue")}
              </Button>
            )}

            <p className="text-center text-sm text-muted-foreground">
              {t("已有账号？", "Already have an account?")}{" "}
              <Link href="/login" className="text-foreground font-medium hover:underline transition-colors">
                {t("返回登录", "Back to sign in")}
              </Link>
            </p>
          </form>
        ) : (
          /* Step 2: Account Info */
          <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3">
            <div className="flex items-center gap-2 rounded-xl border border-border/50 bg-white/70 px-3 py-2 text-sm text-muted-foreground shadow-sm">
              <CheckCircle className="h-4 w-4 shrink-0 text-green-500" />
              <span className="truncate">
                {t("已验证邮箱：", "Verified email: ")}
                <span className="font-medium text-foreground">{email}</span>
              </span>
            </div>

            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
              <Input
                id="signup-username"
                type="text"
                placeholder={t("用户名", "Username")}
                value={username}
                onChange={(event) => {
                  setUsername(event.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                className="h-11 rounded-xl pl-9 bg-white border-border/50 shadow-sm"
                autoFocus
              />
            </div>

            <div className="relative">
              <UserRoundPlus className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
              <Input
                id="signup-fullname"
                type="text"
                placeholder={t("姓名（可选）", "Full name (optional)")}
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                className="h-11 rounded-xl pl-9 bg-white border-border/50 shadow-sm"
              />
            </div>

            <div className="space-y-1.5">
              <div className="relative">
                <Gift className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                <Input
                  id="signup-invite-code"
                  type="text"
                  placeholder={t("邀请码（可选）", "Invite code (optional)")}
                  value={inviteCode}
                  onBlur={() => {
                    validateCurrentInviteCode().catch(error => {
                      console.error("Validate invite code blur failed:", error);
                    });
                  }}
                  onChange={(event) => {
                    setInviteCode(normalizeInviteCode(event.target.value));
                    setInviteCodeMessage("");
                    setIsInviteCodeValid(null);
                    if (errorMessage) setErrorMessage("");
                  }}
                  className="h-11 rounded-xl border-border/50 bg-white pl-9 tracking-wider shadow-sm"
                  disabled={isSubmitting || isValidatingInviteCode}
                />
              </div>
              {inviteCodeMessage ? (
                <div className={isInviteCodeValid ? "text-xs text-emerald-700" : "text-xs text-destructive"}>
                  {isValidatingInviteCode ? t("正在校验邀请码...", "Checking invite code...") : inviteCodeMessage}
                </div>
              ) : null}
            </div>

            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
              <Input
                id="signup-password"
                type="password"
                placeholder={t("至少 6 位密码", "At least 6 characters")}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                className="h-11 rounded-xl pl-9 bg-white border-border/50 shadow-sm"
              />
            </div>

            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
              <Input
                id="signup-password-confirm"
                type="password"
                placeholder={t("再次输入密码", "Enter password again")}
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                className="h-11 rounded-xl pl-9 bg-white border-border/50 shadow-sm"
              />
            </div>

            {errorMessage && (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            {successMessage && (
              <Alert>
                <AlertDescription>{successMessage}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="h-11 w-full rounded-xl mt-1 shadow-sm"
              disabled={!username.trim() || !password.trim() || !confirmPassword.trim() || isSubmitting}
            >
              {isSubmitting ? t("注册中...", "Creating account...") : t("注册", "Sign Up")}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              {t("已有账号？", "Already have an account?")}{" "}
              <Link href="/login" className="text-foreground font-medium hover:underline transition-colors">
                {t("返回登录", "Back to sign in")}
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
